import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button, Coins } from "../components/ui";
import Icon from "../components/Icon";
import { BrandLogo } from "../components/Mascot";

const FEATURES = [
  { icon: "list-checks", title: "Assign chores", text: "Pick from 100+ ready-made missions or write your own." },
  { icon: "badge-check", title: "Approve & reward", text: "Kids tap “I did it”, you approve and award points." },
  { icon: "gift", title: "Family reward shop", text: "Stock treats and let kids spend the coins they earn." },
  { icon: "flame", title: "Streaks & badges", text: "Weekly goals, streaks and badges keep it fun." },
];

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-canvas">
      <header className="max-w-5xl mx-auto px-6 h-20 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BrandLogo size={40} />
          <span className="font-heading font-black text-xl text-ink">ChoreMouse</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate("/login")} data-testid="nav-login">Sign in</Button>
          <Button variant="primary" size="sm" onClick={() => navigate("/register")} data-testid="nav-register">Get started</Button>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-6 pt-8 pb-16 grid md:grid-cols-2 gap-10 items-center">
        <div>
          <motion.span initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary-dark px-4 py-1.5 text-sm font-bold mb-5">
            <Icon name="sparkles" width={16} height={16} /> Chores that kids actually want to do
          </motion.span>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="font-heading font-black text-4xl sm:text-6xl leading-[1.02] text-ink tracking-tight">
            Grow good habits,<br /><span className="text-primary">one chore</span> at a time.
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
            className="mt-5 text-lg text-ink-soft font-medium max-w-md">
            A warm, playful chores & rewards app for the whole family. Parents assign and approve.
            Kids earn coins and spend them in your family reward shop.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="mt-8 flex flex-wrap gap-3">
            <Button size="lg" onClick={() => navigate("/register")} data-testid="hero-get-started">
              Start free <Icon name="arrow-right" width={20} height={20} />
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate("/login?demo=true")} data-testid="hero-demo">
              <Icon name="play" width={18} height={18} /> Try the demo
            </Button>
          </motion.div>
          <button onClick={() => navigate("/kids")} data-testid="hero-kids-door"
            className="mt-6 inline-flex items-center gap-2 text-primary-dark font-bold hover:gap-3 transition-all">
            <Icon name="door-open" width={18} height={18} /> Kids — enter your family code
          </button>
        </div>

        <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1 }}
          className="relative">
          <div className="grain bg-kidzone border-2 border-primary/20 rounded-4xl p-6 shadow-clay">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-11 h-11 rounded-full bg-coral/20 border-2 border-coral grid place-items-center"><Icon name="smile" className="text-coral" /></div>
                <div><div className="font-heading font-extrabold text-ink leading-tight">Mia</div><div className="text-xs text-ink-muted font-semibold">On a 2-day streak 🔥</div></div>
              </div>
              <div className="bg-white rounded-2xl px-3 py-1.5 shadow-sm"><Coins amount={85} className="text-ink text-lg" /></div>
            </div>
            {[
              { t: "Make the bed", p: 5, done: true },
              { t: "Feed the pet", p: 5, done: true },
              { t: "Finish homework", p: 10, done: false },
            ].map((c) => (
              <div key={c.t} className={`flex items-center justify-between bg-white rounded-2xl px-4 py-3 mb-2.5 border-2 ${c.done ? "border-primary/30" : "border-slate-100"}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full grid place-items-center ${c.done ? "bg-primary text-white" : "bg-slate-100 text-ink-muted"}`}>
                    <Icon name={c.done ? "check" : "circle"} width={18} height={18} />
                  </div>
                  <span className={`font-bold ${c.done ? "text-ink-muted line-through" : "text-ink"}`}>{c.t}</span>
                </div>
                <Coins amount={c.p} className="text-sunny-fg" />
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      <section className="max-w-5xl mx-auto px-6 pb-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {FEATURES.map((f) => (
          <div key={f.title} className="bg-white border border-slate-200 rounded-3xl p-6 shadow-soft">
            <div className="w-12 h-12 rounded-2xl bg-primary/10 grid place-items-center mb-4"><Icon name={f.icon} className="text-primary" width={26} height={26} /></div>
            <h3 className="font-heading font-extrabold text-lg text-ink mb-1">{f.title}</h3>
            <p className="text-ink-soft text-sm font-medium">{f.text}</p>
          </div>
        ))}
      </section>

      <section className="max-w-3xl mx-auto px-6 pb-16">
        <div className="relative overflow-hidden grain bg-ink text-white rounded-4xl p-8 sm:p-10 text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-sunny text-sunny-fg px-3 py-1 text-xs font-black uppercase tracking-wider mb-4">
            <Icon name="crown" width={14} height={14} /> ChoreMouse Plus
          </span>
          <h2 className="font-heading font-black text-3xl sm:text-4xl leading-tight">Free to download.<br />Try everything free for 7 days.</h2>
          <p className="mt-4 text-white/80 font-semibold text-lg">
            Then just <span className="text-primary-light">$4.99/mo</span>, or save 42% with
            <span className="text-primary-light"> annual at $34.99/yr</span>.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button size="lg" onClick={() => navigate("/register")} data-testid="pricing-cta">Start your 7-day free trial</Button>
            <span className="text-white/60 text-sm font-semibold">No card needed to explore the demo</span>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm font-semibold text-ink-soft">
          <span>© {new Date().getFullYear()} ChoreMouse</span>
          <div className="flex flex-wrap gap-x-5 gap-y-2 justify-center">
            <Link to="/privacy" className="hover:text-primary" data-testid="footer-privacy">Privacy</Link>
            <Link to="/terms" className="hover:text-primary" data-testid="footer-terms">Terms</Link>
            <Link to="/parents" className="hover:text-primary">For Parents</Link>
            <Link to="/support" className="hover:text-primary">Support</Link>
            <Link to="/delete-account" className="hover:text-primary">Delete account</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
